import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
    Presentation, 
    Download, 
    Play, 
    ChevronLeft, 
    ChevronRight,
    Loader2,
    Clock,
    CheckCircle,
    Plus,
    Eye,
    Trash2,
    FileDown,
    Monitor,
    Sparkles,
    Maximize2,
    X
} from 'lucide-react';

const STYLES = [
    { value: 'professional', label: 'Professional', color: 'bg-blue-500' },
    { value: 'modern', label: 'Modern', color: 'bg-purple-500' },
    { value: 'corporate', label: 'Corporate', color: 'bg-slate-700' },
    { value: 'creative', label: 'Creative', color: 'bg-pink-500' }
];

const Presentations = () => {
    const { api } = useAuth();
    const [presentations, setPresentations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [topics, setTopics] = useState([]);
    const [modules, setModules] = useState([]);
    
    // Form state
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [formData, setFormData] = useState({
        sourceType: 'custom',
        topic_id: '',
        module_id: '',
        title: '',
        content: '',
        style: 'professional',
        num_slides: 10
    });
    
    // Viewer state
    const [viewerOpen, setViewerOpen] = useState(false);
    const [currentPresentation, setCurrentPresentation] = useState(null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [slidesData, setSlidesData] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        fetchPresentations();
        fetchTopicsAndModules();
    }, []);

    const fetchPresentations = async () => {
        try {
            const response = await api.get('/presentations');
            setPresentations(response.data);
        } catch (error) {
            console.error('Failed to fetch presentations:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchTopicsAndModules = async () => {
        try {
            const [topicsRes, modulesRes] = await Promise.all([
                api.get('/topics'),
                api.get('/modules')
            ]);
            setTopics(topicsRes.data);
            setModules(modulesRes.data);
        } catch (error) {
            console.error('Failed to fetch topics/modules:', error);
        }
    };

    const handleGenerate = async () => {
        const { sourceType, topic_id, module_id, title, content, style, num_slides } = formData;
        
        if (sourceType === 'custom' && (!title || !content)) {
            toast.error('Please provide title and content');
            return;
        }
        if (sourceType === 'topic' && !topic_id) {
            toast.error('Please select a topic');
            return;
        }
        if (sourceType === 'module' && !module_id) {
            toast.error('Please select a module');
            return;
        }

        setGenerating(true);
        try {
            const payload = { style, num_slides };
            if (sourceType === 'topic') payload.topic_id = topic_id;
            else if (sourceType === 'module') payload.module_id = module_id;
            else {
                payload.title = title;
                payload.content = content;
            }

            const response = await api.post('/presentations/generate', payload);
            toast.info('Generation started', { description: response.data.message });
            setShowCreateDialog(false);
            
            // Poll for completion
            pollPresentationStatus(response.data.presentation_id);
        } catch (error) {
            toast.error('Failed to generate presentation', {
                description: error.response?.data?.detail || 'An error occurred'
            });
        } finally {
            setGenerating(false);
        }
    };

    const pollPresentationStatus = async (presentationId) => {
        const poll = setInterval(async () => {
            try {
                const response = await api.get(`/presentations/${presentationId}`);
                if (response.data.status === 'completed') {
                    clearInterval(poll);
                    toast.success('Presentation ready!');
                    fetchPresentations();
                } else if (response.data.status === 'failed') {
                    clearInterval(poll);
                    toast.error('Generation failed', { description: response.data.error });
                }
            } catch (error) {
                clearInterval(poll);
            }
        }, 3000);
    };

    const openViewer = async (presentation) => {
        try {
            const response = await api.get(`/presentations/${presentation.id}/html`);
            setSlidesData(response.data);
            setCurrentPresentation(presentation);
            setCurrentSlide(0);
            setViewerOpen(true);
        } catch (error) {
            toast.error('Failed to load presentation');
        }
    };

    const downloadPptx = async (presentationId) => {
        try {
            const response = await api.get(`/presentations/${presentationId}/export/pptx`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'presentation.pptx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Download started');
        } catch (error) {
            toast.error('Failed to download');
        }
    };

    const deletePresentation = async (id) => {
        try {
            await api.delete(`/presentations/${id}`);
            toast.success('Presentation deleted');
            fetchPresentations();
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const handleKeyDown = useCallback((e) => {
        if (!viewerOpen || !slidesData) return;
        if (e.key === 'ArrowRight' || e.key === ' ') {
            setCurrentSlide(prev => Math.min(prev + 1, slidesData.total_slides - 1));
        } else if (e.key === 'ArrowLeft') {
            setCurrentSlide(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Escape') {
            setIsFullscreen(false);
        }
    }, [viewerOpen, slidesData]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    const renderSlide = (slide, style) => {
        const primaryColor = `#${style?.primary_color || '3B82F6'}`;
        const textColor = `#${style?.text_color || '1F2937'}`;
        
        return (
            <div 
                className="w-full h-full flex flex-col p-8 bg-white rounded-lg shadow-2xl"
                style={{ minHeight: '400px' }}
            >
                {slide.type === 'title' ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center">
                        <h1 
                            className="text-4xl md:text-5xl font-bold mb-4"
                            style={{ color: primaryColor }}
                        >
                            {slide.title}
                        </h1>
                        {slide.subtitle && (
                            <p className="text-xl md:text-2xl text-gray-600">
                                {slide.subtitle}
                            </p>
                        )}
                    </div>
                ) : (
                    <>
                        <h2 
                            className="text-2xl md:text-3xl font-bold mb-6 pb-4 border-b-2"
                            style={{ color: primaryColor, borderColor: primaryColor }}
                        >
                            {slide.title}
                        </h2>
                        <div className="flex-1">
                            {slide.bullet_points && (
                                <ul className="space-y-3">
                                    {slide.bullet_points.map((point, idx) => (
                                        <li 
                                            key={idx} 
                                            className="flex items-start gap-3 text-lg"
                                            style={{ color: textColor }}
                                        >
                                            <span 
                                                className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                                                style={{ backgroundColor: primaryColor }}
                                            />
                                            {point}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {slide.content && !slide.bullet_points && (
                                <p className="text-lg" style={{ color: textColor }}>
                                    {slide.content}
                                </p>
                            )}
                        </div>
                    </>
                )}
            </div>
        );
    };

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in" data-testid="presentations-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
                            <Presentation className="h-8 w-8 text-primary" />
                            Presentations
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Generate professional presentations from your training content
                        </p>
                    </div>
                    <Button 
                        onClick={() => setShowCreateDialog(true)}
                        className="gap-2"
                        data-testid="create-presentation-btn"
                    >
                        <Plus className="h-4 w-4" />
                        Create Presentation
                    </Button>
                </div>

                {/* Presentations Grid */}
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : presentations.length === 0 ? (
                    <Card className="text-center py-12">
                        <CardContent>
                            <Presentation className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                            <h3 className="text-lg font-medium mb-2">No presentations yet</h3>
                            <p className="text-muted-foreground mb-4">
                                Generate your first presentation from topics or custom content
                            </p>
                            <Button onClick={() => setShowCreateDialog(true)} className="gap-2">
                                <Sparkles className="h-4 w-4" />
                                Create Your First Presentation
                            </Button>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {presentations.map((pres) => (
                            <Card key={pres.id} className="group hover:shadow-lg transition-shadow">
                                <CardHeader className="pb-3">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1 min-w-0">
                                            <CardTitle className="text-lg truncate">
                                                {pres.title || 'Untitled'}
                                            </CardTitle>
                                            <CardDescription className="flex items-center gap-2 mt-1">
                                                <Clock className="h-3 w-3" />
                                                {new Date(pres.created_at).toLocaleDateString()}
                                            </CardDescription>
                                        </div>
                                        <Badge 
                                            variant={pres.status === 'completed' ? 'default' : 
                                                    pres.status === 'processing' ? 'secondary' : 'destructive'}
                                        >
                                            {pres.status === 'completed' && <CheckCircle className="h-3 w-3 mr-1" />}
                                            {pres.status === 'processing' && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                                            {pres.status}
                                        </Badge>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex items-center gap-2 mb-4">
                                        <div 
                                            className={`w-3 h-3 rounded-full ${
                                                STYLES.find(s => s.value === pres.style)?.color || 'bg-blue-500'
                                            }`} 
                                        />
                                        <span className="text-sm text-muted-foreground capitalize">
                                            {pres.style} • {pres.num_slides} slides
                                        </span>
                                    </div>
                                    
                                    {pres.status === 'processing' && (
                                        <Progress value={pres.progress || 0} className="h-2 mb-4" />
                                    )}
                                    
                                    {pres.status === 'completed' && (
                                        <div className="flex gap-2">
                                            <Button 
                                                size="sm" 
                                                variant="outline"
                                                onClick={() => openViewer(pres)}
                                                className="flex-1 gap-1"
                                                data-testid={`view-pres-${pres.id}`}
                                            >
                                                <Eye className="h-4 w-4" />
                                                View
                                            </Button>
                                            <Button 
                                                size="sm" 
                                                variant="outline"
                                                onClick={() => downloadPptx(pres.id)}
                                                className="flex-1 gap-1"
                                                data-testid={`download-pres-${pres.id}`}
                                            >
                                                <FileDown className="h-4 w-4" />
                                                PPTX
                                            </Button>
                                            <Button 
                                                size="sm" 
                                                variant="ghost"
                                                onClick={() => deletePresentation(pres.id)}
                                                className="text-destructive hover:text-destructive"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {/* Create Dialog */}
                <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                    <DialogContent className="max-w-lg">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Sparkles className="h-5 w-5 text-primary" />
                                Generate Presentation
                            </DialogTitle>
                        </DialogHeader>
                        
                        <div className="space-y-4 mt-4">
                            <div className="space-y-2">
                                <Label>Source</Label>
                                <Select 
                                    value={formData.sourceType}
                                    onValueChange={(v) => setFormData(prev => ({ ...prev, sourceType: v }))}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="custom">Custom Content</SelectItem>
                                        <SelectItem value="topic">From Topic</SelectItem>
                                        <SelectItem value="module">From Module</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {formData.sourceType === 'topic' && (
                                <div className="space-y-2">
                                    <Label>Select Topic</Label>
                                    <Select 
                                        value={formData.topic_id}
                                        onValueChange={(v) => setFormData(prev => ({ ...prev, topic_id: v }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Choose a topic..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {topics.map(t => (
                                                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}

                            {formData.sourceType === 'module' && (
                                <div className="space-y-2">
                                    <Label>Select Module</Label>
                                    <Select 
                                        value={formData.module_id}
                                        onValueChange={(v) => setFormData(prev => ({ ...prev, module_id: v }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Choose a module..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {modules.map(m => (
                                                <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}

                            {formData.sourceType === 'custom' && (
                                <>
                                    <div className="space-y-2">
                                        <Label>Title</Label>
                                        <Input 
                                            value={formData.title}
                                            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                            placeholder="Presentation title..."
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Content</Label>
                                        <Textarea 
                                            value={formData.content}
                                            onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                                            placeholder="Enter the content to turn into slides..."
                                            rows={4}
                                        />
                                    </div>
                                </>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Style</Label>
                                    <Select 
                                        value={formData.style}
                                        onValueChange={(v) => setFormData(prev => ({ ...prev, style: v }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {STYLES.map(s => (
                                                <SelectItem key={s.value} value={s.value}>
                                                    <span className="flex items-center gap-2">
                                                        <span className={`w-3 h-3 rounded-full ${s.color}`} />
                                                        {s.label}
                                                    </span>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Number of Slides</Label>
                                    <Select 
                                        value={String(formData.num_slides)}
                                        onValueChange={(v) => setFormData(prev => ({ ...prev, num_slides: Number(v) }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {[5, 8, 10, 12, 15, 20].map(n => (
                                                <SelectItem key={n} value={String(n)}>{n} slides</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <Button 
                                onClick={handleGenerate}
                                disabled={generating}
                                className="w-full"
                            >
                                {generating ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <Sparkles className="h-4 w-4 mr-2" />
                                )}
                                Generate Presentation
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Slide Viewer Dialog */}
                <Dialog open={viewerOpen} onOpenChange={setViewerOpen}>
                    <DialogContent className={`${isFullscreen ? 'max-w-full h-screen m-0 rounded-none' : 'max-w-5xl'}`}>
                        <DialogHeader>
                            <DialogTitle className="flex items-center justify-between">
                                <span className="flex items-center gap-2">
                                    <Monitor className="h-5 w-5" />
                                    {slidesData?.title || 'Presentation'}
                                </span>
                                <div className="flex items-center gap-2">
                                    <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => setIsFullscreen(!isFullscreen)}
                                    >
                                        <Maximize2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </DialogTitle>
                        </DialogHeader>
                        
                        {slidesData && (
                            <div className="space-y-4">
                                {/* Slide Display */}
                                <div className={`relative bg-gray-100 rounded-lg overflow-hidden ${isFullscreen ? 'h-[calc(100vh-200px)]' : 'aspect-video'}`}>
                                    {slidesData.slides[currentSlide] && 
                                        renderSlide(slidesData.slides[currentSlide], slidesData.style)
                                    }
                                </div>
                                
                                {/* Controls */}
                                <div className="flex items-center justify-between">
                                    <Button 
                                        variant="outline"
                                        onClick={() => setCurrentSlide(prev => Math.max(0, prev - 1))}
                                        disabled={currentSlide === 0}
                                    >
                                        <ChevronLeft className="h-4 w-4 mr-1" />
                                        Previous
                                    </Button>
                                    
                                    <div className="flex items-center gap-4">
                                        <span className="text-sm text-muted-foreground">
                                            Slide {currentSlide + 1} of {slidesData.total_slides}
                                        </span>
                                        <div className="flex gap-1">
                                            {slidesData.slides.map((_, idx) => (
                                                <button
                                                    key={idx}
                                                    onClick={() => setCurrentSlide(idx)}
                                                    className={`w-2 h-2 rounded-full transition-colors ${
                                                        idx === currentSlide ? 'bg-primary' : 'bg-gray-300 hover:bg-gray-400'
                                                    }`}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                    
                                    <Button 
                                        variant="outline"
                                        onClick={() => setCurrentSlide(prev => Math.min(slidesData.total_slides - 1, prev + 1))}
                                        disabled={currentSlide === slidesData.total_slides - 1}
                                    >
                                        Next
                                        <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </div>

                                {/* Speaker Notes */}
                                {slidesData.slides[currentSlide]?.speaker_notes && (
                                    <div className="p-4 bg-muted rounded-lg">
                                        <p className="text-sm font-medium mb-1">Speaker Notes:</p>
                                        <p className="text-sm text-muted-foreground">
                                            {slidesData.slides[currentSlide].speaker_notes}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </MainLayout>
    );
};

export default Presentations;
